import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core'
import { Params } from '@angular/router'

import { CommonModule } from '@angular/common'
import { FilterType } from '../../enums/filter-type.enum'
import { ResourceSlug } from '../../enums/wordpress/wp-resource-slug.enum'
import { Filter } from '../../interfaces/filter.interface'
import { SelectOption } from '../../interfaces/select-option.interface'
import { StripHtmlPipe } from '../../pipes/strip-html.pipe'
import { CatalogueService } from '../../services/catalogue.service'
import { CheckBoxInputComponent } from '../check-box-input/check-box-input.component'
import { MultiTagsInputComponent } from '../multi-tags-input/multi-tags-input.component'
import { SearchInputComponent } from '../search-input/search-input.component'
import { SelectAutocompleteInputComponent } from '../select-autocomplete-input/select-autocomplete-input.component'

@Component({
  selector: 'app-list-filter',
  templateUrl: './list-filter.component.html',
  styleUrls: ['./list-filter.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    StripHtmlPipe,
    MultiTagsInputComponent,
    SelectAutocompleteInputComponent,
    CheckBoxInputComponent,
    SearchInputComponent
  ]
})
export class ListFilterComponent implements OnInit, OnChanges {
  @Input() filterGroups: {
    name?: string
    filters: Filter[]
    isHidden?: boolean
  }[] = []
  @Input() listPath: string
  @Input() selectedOptionIds: Params = []
  @Input() resourceSlug: ResourceSlug

  @Output() selectedOptionsLoaded: EventEmitter<
    {
      label: string
      value: number | string
      queryParam: string
      filterTitle
    }[]
  > = new EventEmitter()
  @Output() selectedOptionsChanged: EventEmitter<{
    propName: string
    value: (string | number)[] | string | number
  }> = new EventEmitter()

  selectedOptions: {
    label: string
    value: string | number
    queryParam: string
    filterTitle: string
  }[] = []

  FilterType = FilterType

  constructor(private catalogueService: CatalogueService) {}

  ngOnInit(): void {
    const taxonomyPromises: Promise<void>[] = []

    // Get Taxonomy terms for filter to use as option.
    this.filterGroups.forEach(
      (filterGroup: { name?: string; filters: Filter[] }) => {
        filterGroup.filters.forEach((filter: Filter) => {
          let filterQueryParams =
            this.selectedOptionIds[filter.queryParam] || []

          if (
            filter.filterType === FilterType.CheckBox ||
            filter.filterType === FilterType.MultiTags ||
            filter.filterType === FilterType.SelectAutocomplete
          ) {
            if (typeof filterQueryParams === 'string') {
              filterQueryParams = [filterQueryParams]
            }
            filter.initialIds = filterQueryParams.map((id) => parseInt(id))
          } else {
            filter.initialValue = filterQueryParams
          }

          // Open active filters.
          if (
            !filter.isOpen &&
            (filter.initialIds?.length || filter.initialValue)
          ) {
            filter.isOpen = true
          }
        })
        filterGroup.filters
          .filter(
            (f) =>
              f.filterType === FilterType.MultiTags ||
              f.filterType === FilterType.CheckBox
          )
          .forEach((filter: Filter) => {
            taxonomyPromises.push(
              this.catalogueService
                .listSelectOptions(filter.slug, {
                  withCount: this.resourceSlug
                })
                .then((filterTaxonomyRes: SelectOption[]) => {
                  filter.options = filterTaxonomyRes.sort(
                    this.sortTaxonomyOptions
                  )

                  // Add to select options.
                  filter.initialIds.forEach((value) => {
                    const selectedOption: SelectOption = filter.options.find(
                      (o) => o.value === value
                    )
                    if (selectedOption) {
                      this.selectedOptions.push({
                        value: selectedOption.value,
                        label: selectedOption.label,
                        queryParam: filter.queryParam,
                        filterTitle: filter.title
                      })
                    }
                  })
                })
            )
          })
      }
    )

    // Emit selected options to parent once loaded.
    Promise.all(taxonomyPromises).then(() => {
      this.selectedOptionsLoaded.emit(this.selectedOptions)
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.selectedOptionIds?.firstChange === false) {
      // Emit selected options to parent once loaded.

      this.selectedOptions = []

      // "Clear all filters" case.
      if (Object.keys(changes.selectedOptionIds.currentValue).length === 0) {
        this.selectedOptions = []
        this.filterGroups.forEach(
          (filterGroup: { name?: string; filters: Filter[] }) => {
            filterGroup.filters.forEach((filter: Filter) => {
              filter.initialIds = []
              filter.initialValue = ''
            })
          }
        )

        this.selectedOptionsLoaded.emit([])
      }

      Object.keys(changes.selectedOptionIds.currentValue).forEach(
        (key: string) => {
          // Flatten filter groups to find filter by query param.
          const filter = this.filterGroups
            .reduce((acc, filterGroup) => acc.concat(filterGroup.filters), [])
            .find((f) => f.queryParam === key)

          if (
            filter &&
            (filter.filterType === FilterType.CheckBox ||
              filter.filterType === FilterType.MultiTags ||
              filter.filterType === FilterType.SelectAutocomplete) &&
            filter.queryParam !== 'toolIds' // Exclude toolIds from selectedOptions as it has its own way of handling selected options.
          ) {
            let selectedOptionIds = changes.selectedOptionIds.currentValue[key]

            if (typeof selectedOptionIds === 'string') {
              selectedOptionIds = [selectedOptionIds]
            }

            if (typeof selectedOptionIds === 'string') {
              selectedOptionIds = [selectedOptionIds]
            }
            filter.initialIds = selectedOptionIds.map((id) => parseInt(id))

            console.log(filter)

            const selectedOptions: SelectOption[] = filter.options.filter(
              (o: SelectOption) => filter.initialIds.includes(o.value)
            )

            this.selectedOptions.push(
              ...selectedOptions.map((option: SelectOption) => ({
                label: option.label,
                value: option.value,
                queryParam: filter.queryParam,
                filterTitle: filter.title
              }))
            )
          }
        }
      )

      this.selectedOptionsLoaded.emit(this.selectedOptions)
    }
  }

  onFilterChanged(filterValue: any, filterOptionParam: string) {
    this.selectedOptionsChanged.emit({
      propName: filterOptionParam,
      value: filterValue
    })
  }

  clearFilterSelectedOptions(filter: Filter) {
    // Clear selected options.
    filter.initialIds = []
    this.selectedOptions = this.selectedOptions.filter(
      (element) => element.queryParam !== filter.queryParam
    )

    this.selectedOptionsChanged.emit({
      propName: filter.queryParam,
      value: []
    })
  }

  sortTaxonomyOptions(a: SelectOption, b: SelectOption): number {
    const termsThatGoesLast = ['Other', 'None', 'Not applicable']
    const termsThatGoesFirst = ['All of the above']

    return termsThatGoesLast.includes(a.label) ||
      termsThatGoesFirst.includes(b.label)
      ? 1
      : -1
  }

  isFilterSelected(filter: Filter): boolean {
    return !!this.selectedOptions.find(
      (element) => element.queryParam === filter.queryParam
    )
  }
}
